import variables from '../../styles/_variables.scss';
import { CodeSecurityPlaceholder } from './cards/placeholder/CodeSecurityPlaceholder';
import { Grade, ReportDisplaySettings, ReportScoreMessages, ScoreLevel, ScoreStyle } from './types';

export const scoreStyles: { [key in Grade]: ScoreStyle } = {
  [Grade.A]: {
    grade: Grade.A,
    gradeLabel: 'A',
    color: variables.colorScoreA,
  },
  [Grade.B]: {
    grade: Grade.B,
    gradeLabel: 'B',
    color: variables.colorScoreB,
  },
  [Grade.C]: {
    grade: Grade.C,
    gradeLabel: 'C',
    color: variables.colorScoreC,
  },
  [Grade.D]: {
    grade: Grade.D,
    gradeLabel: 'D',
    color: variables.colorScoreD,
  },
  [Grade.F]: {
    grade: Grade.F,
    gradeLabel: 'F',
    color: variables.colorScoreF,
  },
};

export const reportScoreMessages: ReportScoreMessages = {
  functionalSecurity: {
    deployedProfiles: {
      [ScoreLevel.Pass]: 'You are safe with the number of profiles in your organization in comparison to the total number of active users.',
      [ScoreLevel.Warning]: 'Your organization seems to have more number of profiles in comparison to the total number of active users.',
      [ScoreLevel.Danger]: 'Your organization seems to have a disproportionate amount of profiles in comparison to the total number of active users.',
    },
    deployedPermissionSets: {
      [ScoreLevel.Pass]: 'You are safe with the number of permission sets in your organization in comparison to the total number of active users.',
      [ScoreLevel.Warning]: 'Your organization seems to have more number of permission sets in comparison to the total number of active users.',
      [ScoreLevel.Danger]: 'Your organization seems to have a disproportionate amount of permission sets in comparison to the total number of active users.',
    },
    permissionSetGroupsUsage: {
      [ScoreLevel.Pass]: 'Good job, you are using permission set groups.',
      [ScoreLevel.Danger]: 'You are not using permission set groups.',
    },
    permissionSetsUsage: {
      [ScoreLevel.Pass]: 'Good job, you are using permission sets.',
      [ScoreLevel.Danger]: 'You are not using permission sets.',
    },
    adminUsers: {
      [ScoreLevel.Pass]: 'You are safe with the number of System Admin permissions assigned.',
      [ScoreLevel.Warning]: 'You have a moderate number of users with System Admin permissions assigned.',
      [ScoreLevel.Danger]: 'You have too many users with System Admin permissions assigned.',
    },
    overdueReleaseUpdates: {
      [ScoreLevel.Pass]: 'You are safe with the number of overdue release updates.',
      [ScoreLevel.Warning]: 'You have a moderate number of overdue release updates.',
      [ScoreLevel.Danger]: 'You have too many overdue release updates.',
    },
  },
  dataSecurity: {
    encryptionUsage: {
      [ScoreLevel.Pass]: 'You are safe using encryption for sensitive data.',
      [ScoreLevel.Danger]: 'You are not using encryption for sensitive data.',
    },
    dataMaskingUsage: {},
    viewAllPermission: {
      [ScoreLevel.Pass]: 'You are safe with the number of users who have access to View All permissions.',
      [ScoreLevel.Warning]: 'You have a moderate number of users with View all permissions.',
      [ScoreLevel.Danger]: 'You have too many users with View All permissions.',
    },
    apiEnabledPermission: {
      [ScoreLevel.Pass]: 'You are safe with the number of users who have access to API permissions.',
      [ScoreLevel.Warning]: 'You have a moderate number of users with API permissions.',
      [ScoreLevel.Danger]: 'You have too many users with API permissions.',
    },
    modifyAllPermission: {
      [ScoreLevel.Pass]: 'You are safe with the number of users who have access to Modify All permissions.',
      [ScoreLevel.Warning]: 'You have a moderate number of users with Modify all permissions.',
      [ScoreLevel.Danger]: 'You have too many users with Modify All permissions.',
    },
    exportPermission: {
      [ScoreLevel.Pass]: 'You are safe with the number of users who have access to Export permissions.',
      [ScoreLevel.Warning]: 'You have a moderate number of users with Export permissions.',
      [ScoreLevel.Danger]: 'You have too many users with Export permissions.',
    },
    publicObjects: {
      [ScoreLevel.Pass]: 'You don\'t have objects with default public access.',
      [ScoreLevel.Danger]: 'You have some objects with default public access.',
    },
  },
  externalAccess: {
    remoteSiteSettings: {
      [ScoreLevel.Pass]: 'You don\'t have any remote site settings and your data is safe.',
      [ScoreLevel.Danger]: 'At least one external site is added as a remote site setting.',
    },
    openIpAddresses: {
      [ScoreLevel.Pass]: 'You don\'t have any trusted IP ranges.',
      [ScoreLevel.Danger]: 'You have a number of IPs from which users can log in without additional steps of verification of their identity.',
    },
    dataExposingPackages: {
      //Hide pass result for case 10304
      //[ScoreLevel.Pass]: 'You don\'t have any installed packages that potentially can send data to third-party apps.',
      [ScoreLevel.Danger]: 'You have packages that potentially can send your data to third-party apps.',
    },
    exportDataPermission: {
      [ScoreLevel.Pass]: 'You are safe with the number of users who have Data Export permission.',
      [ScoreLevel.Warning]: 'You have a moderate number of  users who have Data Export permission.',
      [ScoreLevel.Danger]: 'You have too many users with Data Export Permission.',
    },
    namedCredentials: {
      [ScoreLevel.Pass]: 'You don\'t use named credentials to access external systems to send data. You are safe.',
      [ScoreLevel.Danger]: 'You use named credentials to access external systems and potentially send your data.',
    },
    externalPublicObjects: {
      [ScoreLevel.Pass]: 'Your data is safe. You don\'t have objects with default Public permission for Portal/Site Users.',
      [ScoreLevel.Danger]: 'You have a number of objects with default Public permission for Portal/Site Users. You are at risk of unauthorized access to your data.',
    },
    externalSharingRules: {
      [ScoreLevel.Pass]: 'You don\'t have sharing rules for Portal/Site Users. Your data is safe.',
      [ScoreLevel.Danger]: 'You have a number of sharing rules for Portal/Site Users. You are at risk of unauthorized access to your data.',
    },
    externalQueuesAndGroups: {
      [ScoreLevel.Pass]: 'Your data is safe. You don\'t have queues or public groups with Portal/Site users.',
      [ScoreLevel.Danger]: 'You have queues or public groups with public/site users. You are at risk of unauthorized access to your data.',
    },
  },
  softwareAnalysis: {
    managedPackages: {
      [ScoreLevel.Pass]: 'You are safe with unauthorized managed packages.',
      [ScoreLevel.Danger]: 'You have unauthorized managed packages.',
    },
    unmanagedPackages: {
      [ScoreLevel.Pass]: 'You are safe with unauthorized unmanaged packages.',
      [ScoreLevel.Danger]: 'You have unauthorized unmanaged packages.',
    },
    staticLibraries: {
      [ScoreLevel.Pass]: 'You are safe with static libraries.',
      [ScoreLevel.Danger]: 'You use static libraries.',
    },
    codeCoverage: {
      [ScoreLevel.Pass]: 'You have a good level of test coverage.',
      [ScoreLevel.Warning]: 'The percent of test coverage is close to limit.',
      [ScoreLevel.Danger]: 'The percent of test coverage is low.',
    },
  },
  organizationSecurity: {
    sessionTimeout: {
      [ScoreLevel.Pass]: 'You are safe with session time out settings.',
      [ScoreLevel.Warning]: 'You have a moderate session time out setting.',
      [ScoreLevel.Danger]: 'You have an unsafe session timeout setting.',
    },
    forceLogoutOnTimeout: {
      [ScoreLevel.Pass]: 'You have a safe force logout on session timeout setting.',
      [ScoreLevel.Warning]: 'You have a moderate force logout on session timeout setting.',
      [ScoreLevel.Danger]: 'You have an unsafe force logout on session timeout setting.',
    },
    sessionDomainLock: {
      [ScoreLevel.Pass]: 'You have a safe number of lock sessions from the domain which they were first used.',
      [ScoreLevel.Warning]: 'You have a moderate number of lock sessions from the domain which they were first used.',
      [ScoreLevel.Danger]: 'You have an unsafe number of lock sessions from the domain which they were first used.',
    },
    sessionIpLock: {
      [ScoreLevel.Pass]: 'You have a safe number of lock sessions for the IP address from which they originated.',
      [ScoreLevel.Warning]: 'You have a moderate number of lock sessions for the IP address from which they originated.',
      [ScoreLevel.Danger]: 'You have an unsafe number of lock sessions for the IP address from which they originated.',
    },
    trustedIpRanges: {
      [ScoreLevel.Pass]: 'You have a safe number of trusted IP ranges for your organization.',
      [ScoreLevel.Danger]: 'You have an unsafe number of trusted IP ranges for your organization.',
    },
    invalidLoginAttempts: {
      [ScoreLevel.Pass]: 'You have a safe number for maximum invalid login attempts.',
      [ScoreLevel.Danger]: 'You have an unsafe number for maximum invalid login attempts.',
    },
    expiredCertificates: {
      [ScoreLevel.Pass]: 'You have no expired certificates.',
      [ScoreLevel.Danger]: 'You have too many expired certificates.',
    },
    passwordComplexity: {
      [ScoreLevel.Pass]: 'You have a safe password complexity requirement.',
      [ScoreLevel.Warning]: 'You have a moderate password complexity requirement.',
      [ScoreLevel.Danger]: 'You have an unsafe password complexity requirement.',
    },
    passwordExpirationDays: {
      [ScoreLevel.Pass]: 'You have a safe password expiration timeout.',
      [ScoreLevel.Warning]: 'You have a moderate password expiration timeout.',
      [ScoreLevel.Danger]: 'You have an unsafe password expiration timeout.',
    },
    minPasswordLength: {
      [ScoreLevel.Pass]: 'You have a safe minimum password length.',
      [ScoreLevel.Danger]: 'You have an unsafe minimum password length.',
    },
    passwordLifetime: {
      [ScoreLevel.Pass]: 'You have a safe password lifetime.',
      [ScoreLevel.Danger]: 'You have an unsafe password lifetime.',
    },
    httpOnlyRequired: {
      [ScoreLevel.Pass]: 'Your cookies are safe with HttpOnly attribute.',
      [ScoreLevel.Danger]: 'You don\'t use HttpOnly attribute to protect your cookies.',
    },
    enforceLoginIpRanges: {
      [ScoreLevel.Pass]: 'You have a safe enforce IP ranges on every request setting.',
      [ScoreLevel.Danger]: 'You have an unsafe enforce IP ranges on every request setting.',
    },
  },
  codeSecurity: {
    metaRequireDescription: {
      [ScoreLevel.Pass]: 'Custom fields have a description.',
      [ScoreLevel.Danger]: 'Custom fields should have a description of their function to avoid confusion.',
    },
    metaLimitCustomFields: {
      [ScoreLevel.Pass]: 'Your org does not have too many custom fields per object.',
      [ScoreLevel.Danger]: 'Your org has too many custom fields per object.',
    },
    metaCustomFieldSecurityInStandardObject: {
      [ScoreLevel.Pass]: 'Page Layout or FLS is used to mark CustomField as required.',
      [ScoreLevel.Danger]: 'The custom fields should be marked as required on standard/shared objects through page layout or field level security rather than at system level.',
    },
    vfHardcodedResourceReferences: {
      [ScoreLevel.Pass]: 'Static Resource referencing is used.',
      [ScoreLevel.Danger]: 'Hard-coded path is used. Best practice is to upload the files as a Static Resource.',
    },
    vfUnescapedAttribute: {
      [ScoreLevel.Pass]: 'There are no unescaped parameters in the visualforce code. You are safe from the Unescaped Attribute Value XSS error.',
      [ScoreLevel.Danger]: 'Using unescaped parameters can be a security risk. You may encounter "Unescaped Attribute Value XSS" error in Visualforce. The recommended approach is to use the built-in Visualforce functions for encoding or sanitizing data.',
    },
    vfHardcodedPageLink: {
      [ScoreLevel.Pass]: 'Your org is using the best practice in Visualforce to avoid hard-coded page links in your code. Your page links will continue to work even if the page URL or name changes.',
      [ScoreLevel.Danger]: 'Avoid hard-coded page links in your code to ensure that your links continue to work even if the page URL or name changes. Use Visualforce\'s built-in link generation functionality.',
    },
    jsNoConsole: {
      [ScoreLevel.Pass]: 'You have not used methods on `console`.',
      [ScoreLevel.Danger]: 'It is considered a best practice to avoid using methods on `console`.',
    },
    jsEqualityCheck: {
      [ScoreLevel.Pass]: 'The JavaScript code is your org use the === and !==  strict equality operators that compare both the value and the type of the operands. They do not perform type coercion, and give correct results.',
      [ScoreLevel.Danger]: 'In JavaScript, the === and !== operators should be used instead of == and != operators. The == and != operators perform type coercion before comparing values. It can mask type errors and can result in an unexpected behavior when comparing values of different types.',
    },
    jsAuraUsage: {
      [ScoreLevel.Pass]: 'You have enabled the Validate Aura API usage rule. It will ensure that the Aura APIs are used correctly and efficiently.',
      [ScoreLevel.Danger]: 'It is important to validate the usage of Aura APIs to ensure that they are used correctly and efficiently.',
    },
    jsDotNotation: {
      [ScoreLevel.Pass]: 'You have enforced dot notation in the JavaScript code. It will make your JavaScript code more readable, maintainable, and secure, by preventing potential bugs related to using bracket notation incorrectly.',
      [ScoreLevel.Danger]: 'Enforcing dot notation can help make your JavaScript code more readable, maintainable, and secure, by preventing potential bugs related to using bracket notation incorrectly.',
    },
  },
};

export const reportDisplaySettings: ReportDisplaySettings = {
  functionalSecurity: {
    title: 'Functional security',
    icon: 'badge',
    description: 'Evaluates the functional security of the org by checking the usage of profiles, permission sets, permission set groups, admin access assignments and Salesforce release updates.',
  },
  codeSecurity: {
    title: 'Code security',
    icon: 'apex',
    description: 'Evaluates the quality of code present in the org using the FlosumScan tool. It will scan the code present in your org against a subset of rules. To scan the entire code present in your org/Git repositories using FlosumScan, please contact Flosum Support.',
    placeholder: CodeSecurityPlaceholder,
  },
  dataSecurity: {
    title: 'Data security',
    icon: 'database',
    description: 'Evaluates the data security of the org based on data encryption, data access permissions, and the data made public.',
  },
  externalAccess: {
    title: 'External access',
    icon: 'group',
    description: 'Evaluates external access to the org by checking the usage of remote site settings, named credentials, data/objects accessible to portal/site users, third party apps.',
  },
  softwareAnalysis: {
    title: 'Software composition analysis',
    icon: 'privacy',
    description: 'Evaluates the software composition of the org based on the usage of managed and unmanaged packages, static libraries, and code coverage percentage.',
  },
  organizationSecurity: {
    title: 'Org security',
    icon: 'building',
    description: 'Evaluates the organization security based on your session settings, password policies, network access and certificate management.',
  },
};
